/**
 * Transcript Viewer Page
 *
 * Real-time speech-to-text transcription display
 * with customizable appearance settings (WCAG 2.1 AA compliant)
 *
 * Supports two modes:
 * - Cloud mode: connects via WebSocket to the Polygraf server document system.
 *   This includes a live HTTP writer (the desktop Writer): the server bridges
 *   its pushes to socket subscribers as small deltas. HTTP polling of
 *   `/api/writer/:id/text` survives only as a slow fallback when the socket
 *   cannot connect (card 422).
 * - Local mode: polls text from the local source via server proxy, renders natively
 */

import {
	chooseShownDocument,
	createFollowTracker,
	cssFontFamily,
	DEFAULT_TRANSCRIPT_VIEWER_SETTINGS,
	type DocumentState,
	effectiveTranscriptSettings,
	FOLLOW_LAYOUT_SETTLE_MS,
	type FollowTracker,
	getNativeFontScale as getNativeFontScaleShared,
	isFollowReleaseKey,
	isNativeWebView as isNativeWebViewShared,
	LIVE_SOCKET_FALLBACK_AFTER_MS,
	LOCAL_SOURCE_POLL_MS,
	legibleViewerColors,
	normalizeFontFamily,
	planTranscriptTransport,
	resolveApiBaseUrl,
	SourceType,
	type ThemedTranscriptSettings,
	viewerRoomPath,
	withColorsFollowTheme,
} from '@polygraf/shared'
import DOMPurify from 'dompurify'
import {
	AArrowDown,
	AArrowUp,
	ArrowDownToLine,
	ArrowLeft,
	ChevronsDown,
	Lock,
	LogIn,
	Maximize2,
	Minimize2,
	RefreshCw,
	Settings,
	Wifi,
	WifiOff,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useIsStandaloneViewer } from '@/bootstrap'
import { SourceSelector } from '@/components/source-selector'
import { Spinner } from '@/components/ui'
import { IphoneFullscreenHint } from '@/components/viewer/iphone-fullscreen-hint'
import { ViewerBreadcrumb } from '@/components/viewer/viewer-breadcrumb'
import { Sentry } from '@/observability/sentry'
import { trpc } from '@/trpc/client'
import {
	type TranscriptPresetKey,
	TranscriptSettingsPanel,
} from '../components/viewer/transcript-settings-panel'
import { useImmersiveDoubleTap } from '../hooks/use-immersive-double-tap'
import { useLocalSourceText } from '../hooks/use-local-source-text'
import { usePageTitle } from '../hooks/use-page-title'
import { useSegmentColorGuard } from '../hooks/use-segment-color-guard'
import { useSocketDownFor } from '../hooks/use-socket-down-for'
import { useSocketRetry } from '../hooks/use-socket-retry'
import { useTranscriptAnnouncer } from '../hooks/use-transcript-announcer'
import { useTranscriptSocket } from '../hooks/use-transcript-socket'
import { useAppTheme } from '../lib/app-theme'
import { useSession } from '../lib/auth-client'
import { exitBrowserFullscreen } from '../lib/exit-browser-fullscreen'

// Same problem #324 fixed inline for the build-time VITE_API_URL —
// pass-2 deepening generalised the fallback ladder into shared, so this
// page consumes the canonical resolver and the native bridge wins
// transparently under the mobile WebView.
const API_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL, 'http://localhost:4000')

/** True when running inside the React Native WebView (mobile app has its own header) */
const isNativeWebView = isNativeWebViewShared()

const BASE_FONT_SIZE = 16

// Font-size bounds shared by the settings slider and the footer stepper —
// same 12-96px range the shared TranscriptViewerSettings schema validates
// (MU #11: ceiling raised from 48 to ~96 for low-vision readers).
const MIN_FONT_SIZE = 12
const MAX_FONT_SIZE = 96
const FONT_SIZE_STEP = 2

/** Clamp a font size to the supported 12-96px range */
function clampFontSize(size: number): number {
	return Math.min(MAX_FONT_SIZE, Math.max(MIN_FONT_SIZE, size))
}

const DEFAULT_SETTINGS: ThemedTranscriptSettings = {
	...DEFAULT_TRANSCRIPT_VIEWER_SETTINGS,
	fontSize: BASE_FONT_SIZE,
	colorsFollowTheme: true,
}

// Standalone viewer keeps the two high-visibility presets it gained for MU.
const STANDALONE_PRESETS: readonly TranscriptPresetKey[] = [
	'light',
	'dark',
	'highContrast',
	'sepia',
	'inverted',
	'yellowBlack',
	'default',
]

/** Get the system font scale from the native bridge (defaults to 1.0) */
function getNativeFontScale(): number {
	const scale = getNativeFontScaleShared()
	return scale && scale > 0 ? scale : 1.0
}

/**
 * Report a repaired colour pair once per page load.
 *
 * The repair itself is silent by design — the reader gets legible colours and
 * nothing else changes. But nothing counted it, which is why MP-182 ran
 * unnoticed on one Windows browser for three months. One warning per load is
 * enough to make the bug class visible in Sentry without adding noise.
 */
let colorRepairReported = false
function reportColorRepair(reason: 'unparseable' | 'illegible' | 'translucent'): void {
	if (colorRepairReported) return
	colorRepairReported = true
	Sentry.captureMessage(`viewer colour pair repaired (${reason})`, 'warning')
}

// Load settings from localStorage
function loadSettings(): ThemedTranscriptSettings {
	try {
		const saved = localStorage.getItem('transcriptViewerSettings')
		if (saved) {
			// autoScroll is intentionally not restored: the viewer always opens
			// following the live edge (see saveSettings). Older builds persisted
			// it, so an existing payload can still carry `autoScroll: false` —
			// overriding it after the spread drops that too.
			const stored = JSON.parse(saved)
			const loaded = { ...DEFAULT_SETTINGS, ...stored, autoScroll: true }
			// A stored illegible text/background pair survives every code fix
			// (MP-182, one Windows browser for three months) — validate it here.
			// An unreadable pair is replaced by the default one, which follows the theme.
			return withColorsFollowTheme(
				legibleViewerColors(
					{ ...loaded, fontFamily: normalizeFontFamily(loaded.fontFamily) },
					DEFAULT_SETTINGS,
					reportColorRepair,
				),
				stored,
			)
		}
	} catch {
		// Ignore errors
	}
	// No saved settings — apply system font scale for native WebView
	const fontScale = getNativeFontScale()
	const scaledFontSize = Math.round(clampFontSize(BASE_FONT_SIZE * fontScale))
	return { ...DEFAULT_SETTINGS, fontSize: scaledFontSize }
}

// Save settings to localStorage.
//
// Every appearance setting persists, but auto-scroll deliberately does not:
// opening the transcript viewer must always start following the live edge
// (MU 17. 9. 2026). Persisting it meant a single upward scroll — which
// releases follow — silently wrote `false` and every later session opened
// frozen. Stored appearance settings are left untouched.
function saveSettings(settings: ThemedTranscriptSettings): void {
	try {
		const { autoScroll: _autoScroll, ...persisted } = settings
		localStorage.setItem('transcriptViewerSettings', JSON.stringify(persisted))
	} catch {
		// Ignore errors
	}
}

export default function TranscriptViewerPage() {
	const { t } = useTranslation()
	const { sourceId } = useParams<{ sourceId: string }>()
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const urlParam = searchParams.get('url')
	// `stored` is what persists; `settings` is what paints. They differ only in
	// the colours of a reader who never picked their own: those follow the theme.
	const [stored, setSettings] = useState<ThemedTranscriptSettings>(loadSettings)
	const theme = useAppTheme()
	const settings = useMemo(() => effectiveTranscriptSettings(stored, theme), [stored, theme])
	const guardSegmentColors = useSegmentColorGuard(settings.backgroundColor, settings.textColor)
	const [showSettings, setShowSettings] = useState(false)
	/**
	 * Immersive mode: a tap in the middle of the transcript hides every
	 * control and leaves the text alone on screen (MU 6. 8. 2026 — the old
	 * mobile app had this and the new one lost it). Tap again to bring the
	 * controls back; Escape does the same for keyboard users.
	 *
	 * This is not the Fullscreen API (`isFullscreen`): that one cannot be
	 * entered from inside the mobile app's WebView, and it hides the browser
	 * chrome rather than ours.
	 */
	const [immersive, setImmersive] = useState(false)
	const [isFullscreen, setIsFullscreen] = useState(false)
	const contentRef = useRef<HTMLDivElement>(null)
	/**
	 * Timestamp until which the release logic ignores upward scrolls, because a
	 * layout change (immersive toggle, rotation, fullscreen) is still settling.
	 * A ref, not state: the scroll listener is installed once and must read the
	 * current value without being torn down and re-attached.
	 */
	const layoutSettleUntilRef = useRef(0)
	// Shared by the scroll listener and the follow loop, which rebases it
	// after moving `scrollTop` itself.
	const followTrackerRef = useRef<FollowTracker | null>(null)
	const containerRef = useRef<HTMLDivElement>(null)

	// Local mode: renders text natively via server proxy (instead of cloud WebSocket)
	const isLocalMode = !!urlParam

	// Fetch service info with siblings for source selector (cloud mode only).
	// `isFetched` gates the transport pick below: before serviceInfo settles we
	// must not open the WS (default isLive=false) and then tear it down when
	// isLive flips true — that race blanked / flickered the live transcript
	// (writer→viewer path, Ondra 10.07 + meeting action).
	const {
		data: serviceInfo,
		error: serviceInfoError,
		isFetched: serviceInfoFetched,
	} = trpc.rooms.getService.useQuery(
		{ serviceId: sourceId ?? '' },
		{
			enabled: !!sourceId && !isLocalMode,
			retry: false,
			// Poll while the tab is open so a viewer parked on the waiting
			// screen transitions when a session-backed writer starts — the
			// same 15s cadence the screen viewer uses for its liveness poll
			// (MP-180).
			refetchInterval: 15_000,
		},
	)
	const isServiceNotFound = !isLocalMode && serviceInfoError?.data?.code === 'NOT_FOUND'
	const isServiceResolved = isLocalMode || !sourceId || serviceInfoFetched || isServiceNotFound
	usePageTitle(serviceInfo?.name ?? t('viewer.transcript.title'))

	// A TRANSCRIPT service backed by an ACTIVE live session must render that
	// session's document — the same policy `view-path.ts` applies to the room
	// tiles (`/view/session/{liveDocumentId}`). This route only knew the
	// legacy per-service HTTP-writer live mode, so a direct link / QR /
	// deep link to `/view/transcript/<id>` sat on "Čeká na přepisovatele"
	// forever while the web test writer was streaming into the bound session
	// (found 2026-08-04 verifying MU #10; the shape of Ondra's 10.7. report).
	// `replace` keeps history clean for the back button.
	const liveDocumentId = serviceInfo?.liveDocumentId ?? null
	useEffect(() => {
		if (liveDocumentId) {
			navigate(`/view/session/${liveDocumentId}`, { replace: true })
		}
	}, [liveDocumentId, navigate])

	// Live mode: cloud service with an active HTTP writer. Read over the socket
	// like any cloud document (card 422) — this flag now only decides whether
	// an HTTP fallback exists when the socket cannot connect.
	const isLiveMode = isServiceResolved && !isLocalMode && serviceInfo?.isLive === true

	// Connect to WebSocket (cloud mode, live HTTP writer included; disabled for
	// local mode, and when the service doesn't exist — no point holding a stale
	// socket). Waiting for `isServiceResolved` still matters: the socket must
	// not open for the `?url=`-less local route before we know which one it is.
	// Deliberately not part of `planTranscriptTransport` below: that plan's
	// inputs (connected, down for) come from this socket, so the socket's own
	// on/off has to be decided first.
	const socketEnabled = isServiceResolved && !isLocalMode && !isServiceNotFound
	const {
		document: cloudDocument,
		isConnected,
		isConnecting,
		hasWriter,
		activeParagraphId,
		isWriterTyping,
		error: cloudError,
		accessDenied,
		isSocketCurrent,
		reconnect,
	} = useTranscriptSocket({
		documentId: socketEnabled ? (sourceId ?? '') : '',
		enabled: socketEnabled,
	})

	// Card 422: live mode used to poll `/api/writer/:id/text` every 500 ms
	// forever (2.6 req/s per viewer on prod, background tabs included). Now
	// the socket carries it, and HTTP polls — every 5 s, paused while the page
	// is hidden — only while the socket cannot connect. Local mode is unchanged.
	const socketDownForMs = useSocketDownFor(
		socketEnabled,
		isConnected,
		LIVE_SOCKET_FALLBACK_AFTER_MS,
	)
	const transport = planTranscriptTransport({
		isLocalMode,
		isLive: isLiveMode,
		socketConnected: isConnected,
		socketAccessDenied: accessDenied,
		socketDownForMs,
	})
	const isHttpFallback = !isLocalMode && transport.httpPoll !== null
	// `PolygrafConnection` gives up after 5 attempts (~31 s). While the
	// fallback polls, keep restarting it (visible page only) so the fallback
	// ends — `isConnected` flips, `socketDownForMs` goes null, `httpPoll` null,
	// and the HTTP poll's `enabled` drops, aborting it — once the socket can
	// connect again, instead of polling every 5 s for the rest of the lecture.
	useSocketRetry(transport.socketRetryMs, isConnected, reconnect)
	/** Text comes from the HTTP poll (local mode, or the live fallback). */
	const isPolledMode = isLocalMode || isHttpFallback

	// MP-215 (UAT B5): the WS subscribe was refused by the space/room ACL.
	// Anonymous viewers get a sign-in CTA that round-trips back to this exact
	// viewer URL (same `/auth?returnTo=` mechanism AuthGuard uses); signed-in
	// viewers get a no-access explanation. Terminal state — no retry affordance.
	const location = useLocation()
	const { data: authSession } = useSession()
	const isAuthenticated = !!authSession?.user
	const loginHref = `/auth?returnTo=${encodeURIComponent(location.pathname + location.search)}`

	// `/auth` and `/` exist only in the full app's router. In the prebundled
	// viewer they match nothing, so following either unmounts the page to a
	// blank WebView (#647's reasoning) — hide both there. The icon and title
	// still identify the state; note `accessDenied.noAccess` is gated on
	// `isAuthenticated`, and the bundled viewer has no cookie session (the
	// shell passes its token over the bridge), so an anonymous standalone
	// viewer is left with the title alone. If that reads too bare, add a
	// standalone-specific line rather than restoring the links.
	//
	// Of the two gated states only serviceNotFound is actually reachable in
	// the transcript bundle today — an empty bridge sourceUrl yields `?url=`,
	// which is not local mode, so getService('local') 404s. The accessDenied
	// gate is defensive: its socket is disabled in that entry.
	//
	// Keyed on the router rather than `isNativeWebView`, which is also true in
	// the mobile session WebView, where the target is polygraf.app and both
	// links work.
	const isStandaloneViewer = useIsStandaloneViewer()

	// Poll text from the local source, or from the live HTTP writer while the
	// socket cannot connect
	const {
		document: polledDocument,
		rawHtml: polledRawHtml,
		isLoading: isPolledLoading,
		error: polledError,
		isWaitingForWriter: isPolledWaitingForWriter,
		refresh: polledRefresh,
	} = useLocalSourceText({
		url: urlParam ?? '',
		fetchUrl: isHttpFallback ? `${API_URL}/api/writer/${sourceId}/text` : undefined,
		pollInterval: transport.httpPoll?.intervalMs ?? LOCAL_SOURCE_POLL_MS,
		pauseWhileHidden: transport.httpPoll?.pauseWhileHidden ?? false,
		paceAfterUpdates: transport.httpPoll?.paceAfterUpdates ?? false,
		enabled: isPolledMode,
	})

	// Backward-compatible aliases for the local mode UI references
	const isLocalLoading = isPolledLoading
	const localError = polledError
	const localRefresh = polledRefresh

	// Unified references — all modes use the same rendering pipeline. Across a
	// transport change keep what is on screen until the new transport has
	// something at least as current: the fallback's first poll, or the
	// reconnected socket's snapshot. Switching to the socket's copy from before
	// an outage cut the transcript short, clamped the scroll and "reloaded"
	// (MU N3c, 7. 10. 2026; chooseShownDocument in @polygraf/shared).
	const shownRef = useRef<{ sourceId: string | undefined; document: DocumentState | null }>({
		sourceId,
		document: null,
	})
	const document = chooseShownDocument({
		isLocalMode,
		isHttpFallback,
		polled: polledDocument,
		socket: cloudDocument,
		socketCurrent: isSocketCurrent,
		shown: shownRef.current.sourceId === sourceId ? shownRef.current.document : null,
	})
	useEffect(() => {
		shownRef.current = { sourceId, document }
	}, [sourceId, document])
	const error = isPolledMode ? polledError : cloudError
	// Cloud mode not yet ready to pick a transport — keep the loading shell.
	const isResolvingService = !isServiceResolved

	// F-017: SRs spammed the whole region on each keystroke when aria-live wrapped
	// the document tree. Hook routes only the appended delta to an off-screen
	// announcer at word boundaries — see use-transcript-announcer.ts for rationale.
	// Local/live polling mode has no writer-typing signal; cloud mode passes it in.
	const announcerRef = useTranscriptAnnouncer(document, isPolledMode ? undefined : isWriterTyping)

	// Save settings when they change
	useEffect(() => {
		saveSettings(stored)
	}, [stored])

	// Continuously smooth-scroll to bottom while auto-scroll is enabled
	useEffect(() => {
		if (!settings.autoScroll) return

		let rafId: number
		// The scrollTop this loop last wrote. If it is lower now, someone else
		// scrolled up since (a key, the scrollbar, a screen reader): yield that
		// frame instead of writing over it, and let the scroll tracker decide.
		let lastWritten: number | null = null

		const tick = () => {
			const el = contentRef.current
			if (el) {
				const yielded = lastWritten !== null && el.scrollTop < lastWritten - 1
				const gap = el.scrollHeight - el.scrollTop - el.clientHeight
				if (!yielded && gap > 1) {
					el.scrollTop += Math.max(1, gap * 0.15)
					// The loop's own movement is never the reader returning to
					// the end: its scroll event lands a frame later, after a
					// release or a switch-off, and would re-arm follow.
					followTrackerRef.current?.rebase()
				}
				lastWritten = yielded ? null : el.scrollTop
			}
			rafId = requestAnimationFrame(tick)
		}

		// Release follow on the key press itself, before the browser starts its
		// smooth scroll — see isFollowReleaseKey in @polygraf/shared.
		const handleKeyDown = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement | null
			const inFormField = !!target?.closest?.('input, select, textarea, [contenteditable="true"]')
			if (isFollowReleaseKey({ key: e.key, shiftKey: e.shiftKey, inFormField })) {
				// The key may scroll nothing (focus on a header button, already at
				// the top), so no scroll sample will set the latch: set it here, or
				// scrolling back down cannot re-arm follow (MP-247).
				followTrackerRef.current?.leave()
				setSettings((prev) => (prev.autoScroll ? { ...prev, autoScroll: false } : prev))
			}
		}
		window.addEventListener('keydown', handleKeyDown)

		rafId = requestAnimationFrame(tick)
		return () => {
			cancelAnimationFrame(rafId)
			window.removeEventListener('keydown', handleKeyDown)
		}
	}, [settings.autoScroll])

	// Disable auto-scroll the moment the user scrolls up — wheel for desktop,
	// touch-drag-down (= content moves up = reading upward) for mobile/webview.
	// Matches the current public mobile app behaviour Sváťa flagged on Apr 11.
	useEffect(() => {
		const el = contentRef.current
		if (!el) return

		// Wheel and touch release follow before, or without, any scroll (at
		// the top, a wheel up moves nothing), so they set the tracker's latch
		// themselves, like the release keys (MP-247).
		//
		// The decision lives in `createFollowTracker` / `decideFollowOnScroll`
		// (shared, tested): this logic produced four defects in one day, all of
		// them invisible to touch QA and reachable only by keyboard, scrollbar
		// or screen-reader users. The tracker owns the previous geometry and
		// the latch, so no caller can forget to thread them.
		const track = createFollowTracker(el)
		followTrackerRef.current = track

		const handleWheel = (e: WheelEvent) => {
			if (e.deltaY < 0) {
				track.leave()
				setSettings((prev) => (prev.autoScroll ? { ...prev, autoScroll: false } : prev))
			}
		}

		let touchStartY: number | null = null
		const handleTouchStart = (e: TouchEvent) => {
			touchStartY = e.touches[0]?.clientY ?? null
		}
		const handleTouchMove = (e: TouchEvent) => {
			if (touchStartY === null) return
			const y = e.touches[0]?.clientY
			if (y === undefined) return
			// Finger moving down means content scrolls up — user is reading earlier text.
			if (y - touchStartY > 12) {
				track.leave()
				setSettings((prev) => (prev.autoScroll ? { ...prev, autoScroll: false } : prev))
				touchStartY = null
			}
		}

		// Scrollbar drag, PageUp/arrow keys and screen-reader navigation raise
		// none of the wheel/touch events above, so releasing on an upward
		// scroll is the only thing that serves them. (CodeRabbit on #633.)
		const handleScroll = () => {
			const action = track({ settling: performance.now() < layoutSettleUntilRef.current })
			if (action === 'release') {
				setSettings((prev) => (prev.autoScroll ? { ...prev, autoScroll: false } : prev))
			} else if (action === 'rearm') {
				// Scrolling back down to the end re-arms live follow (MU 17. 9. 2026).
				setSettings((prev) => (prev.autoScroll ? prev : { ...prev, autoScroll: true }))
			}
		}

		// Every cause of the false release resizes this element: hiding the
		// header for immersive mode, rotating the device, entering or leaving
		// fullscreen. Watching the box itself catches all of them — including
		// the ones React does not drive — without naming any of them.
		const observer = new ResizeObserver(() => {
			layoutSettleUntilRef.current = performance.now() + FOLLOW_LAYOUT_SETTLE_MS
		})
		observer.observe(el)

		el.addEventListener('wheel', handleWheel, { passive: true })
		el.addEventListener('touchstart', handleTouchStart, { passive: true })
		el.addEventListener('touchmove', handleTouchMove, { passive: true })
		el.addEventListener('scroll', handleScroll, { passive: true })
		return () => {
			observer.disconnect()
			el.removeEventListener('wheel', handleWheel)
			el.removeEventListener('touchstart', handleTouchStart)
			el.removeEventListener('touchmove', handleTouchMove)
			el.removeEventListener('scroll', handleScroll)
			followTrackerRef.current = null
		}
	}, [])

	// Handle fullscreen changes
	useEffect(() => {
		const handleFullscreenChange = () => {
			const active = !!globalThis.document.fullscreenElement
			setIsFullscreen(active)
			// Leaving fullscreen through the browser's own Escape must also bring
			// the controls back, or the button reads "exit" with nothing to exit.
			if (!active) setImmersive(false)
		}
		globalThis.document.addEventListener('fullscreenchange', handleFullscreenChange)
		return () => {
			globalThis.document.removeEventListener('fullscreenchange', handleFullscreenChange)
		}
	}, [])

	const scrollToBottom = useCallback(() => {
		if (contentRef.current) {
			contentRef.current.scrollTo({
				top: contentRef.current.scrollHeight,
				behavior: 'smooth',
			})
		}
		// "Jump to end" must also resume live follow: after the user scrolls up
		// (which disables auto-scroll), tapping it should re-pin to newly
		// transcribed text, not just scroll once. (MP r11/r130)
		setSettings((prev) => (prev.autoScroll ? prev : { ...prev, autoScroll: true }))
	}, [])

	// U14: quick font-size stepper in the header — same settings.fontSize the
	// settings-panel slider drives, clamped to the same 12-96px bounds.
	const adjustFontSize = useCallback((delta: number) => {
		setSettings((prev) => ({ ...prev, fontSize: clampFontSize(prev.fontSize + delta) }))
	}, [])

	/**
	 * Double tap in the text leaves immersive mode; it never enters it — the
	 * header button and F do (MU 25. 9. 2026). Taps on controls, the tap that
	 * ends a text selection, a scroll and a pinch never count. See
	 * decideImmersiveTap.
	 */
	const exitImmersiveFromTap = useCallback(() => {
		setShowSettings(false)
		setImmersive(false)
		exitBrowserFullscreen()
	}, [])
	const contentTap = useImmersiveDoubleTap(immersive, exitImmersiveFromTap)

	// The mobile app draws its own native header above this WebView. Tell it to
	// hide that header too, otherwise "everything disappears" leaves one bar
	// behind and the mode looks broken.
	useEffect(() => {
		if (!isNativeWebView) return
		try {
			const bridge = (
				window as unknown as {
					ReactNativeWebView?: { postMessage: (s: string) => void }
				}
			).ReactNativeWebView
			bridge?.postMessage(
				JSON.stringify({ source: 'transcript-viewer', type: 'immersive', immersive }),
			)
		} catch (err) {
			// The host is a plain browser, or the payload failed to stringify.
			// Immersive mode still works here; only the native header lags.
			console.warn('[TranscriptViewer] immersive bridge failed:', err)
		}
	}, [immersive])

	const toggleFullscreen = useCallback(async () => {
		setShowSettings(false)
		setImmersive((prev) => !prev)
		if (isNativeWebView || !containerRef.current) return
		// The Fullscreen API is a nice-to-have on top of the hidden chrome: it
		// needs a user gesture and a permissive iframe policy, and rejects
		// otherwise. A rejection must not undo the mode the user just asked for.
		try {
			if (!globalThis.document.fullscreenElement) {
				await containerRef.current.requestFullscreen?.()
			} else {
				await globalThis.document.exitFullscreen()
			}
		} catch (err) {
			console.warn('[TranscriptViewer] fullscreen request refused:', err)
		}
	}, [])

	// Keyboard shortcuts
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			// Escape to exit fullscreen or go back
			if (e.key === 'Escape') {
				if (showSettings) {
					setShowSettings(false)
				} else if (immersive) {
					setImmersive(false)
				} else if (!isFullscreen) {
					navigate(-1)
				}
			}
			// F to toggle fullscreen
			if (e.key === 'f' || e.key === 'F') {
				if (!e.ctrlKey && !e.metaKey) {
					toggleFullscreen()
				}
			}
			// S to toggle settings
			if ((e.key === 's' || e.key === 'S') && !e.ctrlKey && !e.metaKey) {
				setShowSettings((prev) => !prev)
			}
			// End to scroll to bottom
			if (e.key === 'End') {
				scrollToBottom()
			}
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [navigate, isFullscreen, immersive, showSettings, toggleFullscreen, scrollToBottom])

	return (
		<div
			ref={containerRef}
			className="flex flex-col h-screen-dynamic"
			style={{ backgroundColor: settings.backgroundColor }}
		>
			<IphoneFullscreenHint active={immersive} />
			{/* Header. Full-strength divider: base-300/20 over the default white
			    ground composites to ~1.02:1 and does not render at all. #628 fixed
			    that on the screen viewer, which left the two headers mismatched in
			    the other direction — MU compares them side by side. */}
			{!immersive && (
				// Wraps rather than truncates (MU, 7. 10. 2026): with the phone's
				// largest text size the title, writer and status each run onto more
				// lines, and once the title column would drop under ~8 em the
				// buttons move to a row of their own. At the default size the
				// basis is 128 px, so a phone keeps one row.
				<header className="flex flex-wrap items-center justify-between gap-x-2 px-3 py-2 border-b border-base-300">
					<div className="flex items-center gap-2 min-w-0 grow basis-[calc(8rem*var(--text-scale,1))]">
						{!isFullscreen && !isNativeWebView && (
							<Link
								to={
									isLocalMode
										? '/local'
										: serviceInfo
											? viewerRoomPath(serviceInfo.room.spaceId, serviceInfo.room.id)
											: '/'
								}
								className="p-2.5 rounded-lg hover:bg-black/10 transition-colors shrink-0"
								style={{ color: settings.textColor }}
								aria-label={t('common.back')}
							>
								<ArrowLeft size={22} />
							</Link>
						)}
						<div className="min-w-0">
							<h1
								className="text-base font-semibold break-words hyphens-auto"
								style={{ color: settings.textColor }}
							>
								{serviceInfo?.name ?? document?.title ?? t('viewer.transcript.title')}
							</h1>
							{!isLocalMode && serviceInfo?.room && (
								<ViewerBreadcrumb
									room={serviceInfo.room}
									linked={!isNativeWebView}
									wrap
									style={{ color: settings.textColor }}
								/>
							)}
							{/* MP-187: show who is transcribing, when the writer's name is known. */}
							{document?.writerName && (
								<div
									className="text-sm opacity-70 break-words hyphens-auto"
									style={{ color: settings.textColor }}
								>
									{t('viewer.transcript.transcribedBy', { name: document.writerName })}
								</div>
							)}
							<div className="flex flex-wrap items-center gap-x-2 text-sm min-w-0">
								{isServiceNotFound ? (
									<span className="flex items-center gap-1 text-red-600">
										<WifiOff size={16} />
										{t('viewer.transcript.serviceNotFound')}
									</span>
								) : accessDenied ? (
									<span className="flex items-center gap-1 text-red-600">
										<Lock size={16} />
										{t('viewer.transcript.accessDenied.title')}
									</span>
								) : isResolvingService ? (
									<span
										className="flex items-center gap-1 opacity-80"
										style={{ color: settings.textColor }}
									>
										<Spinner size="sm" />
										{t('viewer.transcript.loadingSource')}
									</span>
								) : isPolledMode ? (
									isLocalLoading ? (
										<span
											className="flex items-center gap-1 opacity-80"
											style={{ color: settings.textColor }}
										>
											<Spinner size="sm" />
											{t('viewer.transcript.loadingSource')}
										</span>
									) : isPolledWaitingForWriter ? (
										<span
											className="flex items-center gap-1 opacity-80"
											style={{ color: settings.textColor }}
										>
											<Spinner size="sm" />
											{t('viewer.transcript.waitingForWriter')}
										</span>
									) : localError ? (
										<span className="flex items-center gap-1 text-red-600">
											<WifiOff size={16} />
											{t('viewer.transcript.sourceError')}
										</span>
									) : (
										<span className="flex items-center gap-1 text-green-600">
											<Wifi size={16} />
											{t('viewer.transcript.sourceConnected')}
										</span>
									)
								) : isConnecting ? (
									<span
										className="flex items-center gap-1 opacity-80"
										style={{ color: settings.textColor }}
									>
										<Spinner size="sm" />
										{t('viewer.transcript.connecting')}
									</span>
								) : isConnected ? (
									<span className="flex items-center gap-1 text-green-600">
										<Wifi size={16} />
										{hasWriter
											? t('viewer.transcript.connected')
											: t('viewer.transcript.waitingForWriter')}
									</span>
								) : (
									<span className="flex items-center gap-1 text-red-600">
										<WifiOff size={16} />
										{t('localSources.disconnected')}
									</span>
								)}
							</div>
						</div>
					</div>

					{/* Source selector - show when multiple transcript sources exist (cloud mode only) */}
					{!isLocalMode &&
						serviceInfo &&
						serviceInfo.siblingServices.filter(
							(s: { type: string }) => s.type === SourceType.TRANSCRIPT,
						).length > 1 && (
							<SourceSelector
								currentSourceId={sourceId ?? ''}
								sources={serviceInfo.siblingServices}
								room={serviceInfo.room}
								filterType={SourceType.TRANSCRIPT}
								textColor={settings.textColor}
							/>
						)}

					<div className="flex gap-1 shrink-0 ml-auto">
						{error && !isServiceNotFound && (
							<button
								className="p-2.5 rounded-lg hover:bg-black/10 transition-colors text-orange-500"
								onClick={
									isHttpFallback
										? () => {
												// Retry the socket too: once it connects the fallback stops.
												reconnect()
												polledRefresh()
											}
										: isLocalMode
											? polledRefresh
											: reconnect
								}
								title={t('viewer.transcript.reconnect')}
							>
								<RefreshCw size={20} />
							</button>
						)}
						{/* Font stepper — same settings.fontSize the settings-panel slider
						    drives, clamped to the same bounds. Lived in a footer until MU
						    4. 9. 2026 asked for every control in one bar. Hidden below
						    `sm` like viewer-toolbar's secondary controls: six icon
						    buttons in a shrink-0 group would squeeze the title to a few
						    characters on a 320 px phone, and the settings-panel slider
						    still reaches the same value there. */}
						<div className="hidden sm:flex gap-1">
							<button
								type="button"
								className="p-2.5 rounded-lg hover:bg-black/10 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
								style={{ color: settings.textColor }}
								onClick={() => adjustFontSize(-FONT_SIZE_STEP)}
								disabled={settings.fontSize <= MIN_FONT_SIZE}
								title={t('viewer.transcript.footer.decreaseFontSize')}
								aria-label={t('viewer.transcript.footer.decreaseFontSize')}
							>
								<AArrowDown size={20} />
							</button>
							<button
								type="button"
								className="p-2.5 rounded-lg hover:bg-black/10 transition-colors disabled:opacity-40 disabled:hover:bg-transparent"
								style={{ color: settings.textColor }}
								onClick={() => adjustFontSize(FONT_SIZE_STEP)}
								disabled={settings.fontSize >= MAX_FONT_SIZE}
								title={t('viewer.transcript.footer.increaseFontSize')}
								aria-label={t('viewer.transcript.footer.increaseFontSize')}
							>
								<AArrowUp size={20} />
							</button>
						</div>
						{/*
						Live-follow toggle, shaped like the zoom lock in the screen viewer
						(MU 4. 9. 2026): pressed = auto-scroll pins the view to the live
						edge; it releases itself the moment the user scrolls up to read
						back (wheel/touch handlers), and re-arms when the reader returns
						to the end. Pressing it while released jumps to the end AND
						re-pins, so it also replaces the old "go to end" button — one
						control instead of a checkbox plus a button that only appeared
						when the checkbox was off.

						The two states must be told apart at a glance on a touch screen
						where :hover can stay latched after a tap (MU 17. 9. 2026), so
						they differ in fill, border AND icon rather than in two similar
						translucent tints: ON is a solid primary chip with a "follow the
						stream" chevron, OFF is an outline with a "jump to the end"
						arrow. The OFF hover tint is `hover-surface`, which no touch
						device ever paints, so hover cannot impersonate ON.
					*/}
						<button
							type="button"
							className={`p-2.5 rounded-lg border-2 transition-colors ${
								settings.autoScroll
									? 'bg-primary border-primary text-primary-content shadow-soft'
									: 'bg-transparent border-current opacity-60 hover-surface active:bg-black/10'
							}`}
							style={settings.autoScroll ? undefined : { color: settings.textColor }}
							onClick={
								settings.autoScroll
									? () => {
											followTrackerRef.current?.disarm()
											setSettings((prev) => ({ ...prev, autoScroll: false }))
										}
									: scrollToBottom
							}
							title={
								settings.autoScroll
									? t('viewer.transcript.followLiveOff')
									: t('viewer.transcript.followLive')
							}
							aria-label={t('viewer.transcript.followLive')}
							aria-pressed={settings.autoScroll}
						>
							{settings.autoScroll ? <ChevronsDown size={20} /> : <ArrowDownToLine size={20} />}
						</button>
						<button
							type="button"
							className="p-2.5 rounded-lg hover:bg-black/10 transition-colors"
							style={{ color: settings.textColor }}
							onClick={toggleFullscreen}
							title={
								immersive || isFullscreen
									? t('viewer.transcript.exitFullscreen')
									: t('viewer.keyboard.fullscreen')
							}
							aria-label={
								immersive || isFullscreen
									? t('viewer.transcript.exitFullscreen')
									: t('viewer.keyboard.fullscreen')
							}
						>
							{immersive || isFullscreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
						</button>
						<button
							className={`p-2.5 rounded-lg transition-colors ${showSettings ? 'bg-primary/20' : 'hover:bg-black/10'}`}
							style={{ color: settings.textColor }}
							onClick={() => setShowSettings(!showSettings)}
							title={t('viewer.transcript.settings.title')}
							aria-expanded={showSettings}
						>
							<Settings size={20} />
						</button>
					</div>
				</header>
			)}

			{/* Settings panel */}
			{showSettings && (
				<TranscriptSettingsPanel
					settings={settings}
					onChange={setSettings}
					onClose={() => setShowSettings(false)}
					t={t}
					defaults={DEFAULT_SETTINGS}
					presets={STANDALONE_PRESETS}
					idPrefix="tv"
					minFontSize={MIN_FONT_SIZE}
					maxFontSize={MAX_FONT_SIZE}
				/>
			)}

			{/* Content — unified rendering for both cloud and local modes */}
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: the double tap only leaves immersive
			    mode; keyboard users enter and leave it through the header button,
			    F and Escape, all wired above. The region itself is focusable
			    (tabIndex) so keys scroll it and screen readers can enter it. */}
			<main
				ref={contentRef}
				// touch-manipulation: iOS would otherwise spend the double tap on
				// its own zoom and the page would never see the second tap.
				// Trade-off: this also turns off iOS's own double-tap-to-zoom here, which
				// some low-vision readers use. Pinch zoom still works (manipulation keeps
				// it), and the font-size stepper stays in the header.
				className="flex-1 overflow-y-auto p-6 touch-manipulation"
				style={{
					backgroundColor: settings.backgroundColor,
					color: settings.textColor,
					fontSize: `${settings.fontSize}px`,
					fontFamily: cssFontFamily(settings.fontFamily),
					fontWeight: settings.fontWeight,
					lineHeight: settings.lineHeight,
					overflowWrap: 'break-word',
					wordBreak: 'break-word',
				}}
				role="region"
				// Focusable so a screen reader or keyboard user can move straight into
				// the text and PageUp/Down scroll it (retest 24. 9. 2026: the text was
				// only reachable by selecting it).
				// biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be keyboard-reachable (WCAG 2.1.1, axe scrollable-region-focusable).
				tabIndex={0}
				aria-label={t('viewer.transcript.title')}
				onPointerDown={contentTap.onPointerDown}
				onClick={contentTap.onClick}
			>
				<div className="w-full max-w-none">
					{isServiceNotFound ? (
						/* Cloud mode: explicit "service not found" state when getService 404s */
						<div className="flex flex-col items-center justify-center h-64 text-center">
							<WifiOff size={48} className="mb-4 opacity-30" />
							<p className="text-lg font-medium mb-2">{t('viewer.transcript.serviceNotFound')}</p>
							<p className="opacity-80 mb-4">{t('viewer.transcript.serviceNotFoundDesc')}</p>
							{!isStandaloneViewer && (
								<Link
									to="/"
									className="px-4 py-2 rounded-lg border hover:bg-black/10 transition-colors"
									style={{ borderColor: `${settings.textColor}40`, color: settings.textColor }}
								>
									{t('viewer.transcript.backHome')}
								</Link>
							)}
						</div>
					) : accessDenied ? (
						/* Cloud mode: the WS subscribe was denied by the space/room ACL
						   (MP-215). Anonymous → sign-in CTA; signed-in → no-access note. */
						<div className="flex flex-col items-center justify-center h-64 text-center">
							<Lock size={48} className="mb-4 opacity-30" />
							<p className="text-lg font-medium mb-2">
								{t('viewer.transcript.accessDenied.title')}
							</p>
							{isAuthenticated && (
								<p className="opacity-80 mb-4 max-w-md">
									{t('viewer.transcript.accessDenied.noAccess')}
								</p>
							)}
							{/* Wrapping the row, not each link: with both gated out the flex
							    container would still render its gap and margin. */}
							{!isStandaloneViewer && (
								<div className="flex flex-wrap items-center justify-center gap-2">
									{!isAuthenticated && (
										<Link
											to={loginHref}
											className="flex items-center gap-2 px-4 py-2 rounded-lg border hover:bg-black/10 transition-colors font-medium"
											style={{ borderColor: `${settings.textColor}40`, color: settings.textColor }}
										>
											<LogIn size={16} />
											{t('viewer.transcript.accessDenied.signIn')}
										</Link>
									)}
									<Link
										to="/"
										className="px-4 py-2 rounded-lg border hover:bg-black/10 transition-colors"
										style={{ borderColor: `${settings.textColor}40`, color: settings.textColor }}
									>
										{t('viewer.transcript.backHome')}
									</Link>
								</div>
							)}
						</div>
					) : isResolvingService || isPolledMode ? (
						/* Service resolving / Local / live HTTP fallback: loading / waiting / error / content */
						isResolvingService || (isLocalLoading && !document && !polledRawHtml) ? (
							<div className="flex flex-col items-center justify-center h-64 text-center">
								<span className="mb-4 opacity-30">
									<Spinner size="lg" />
								</span>
								<p className="text-lg font-medium mb-2">{t('viewer.transcript.loadingSource')}</p>
							</div>
						) : isPolledWaitingForWriter && !document && !polledRawHtml ? (
							<div className="flex flex-col items-center justify-center h-64 text-center">
								<span className="mb-4 opacity-30">
									<Spinner size="lg" />
								</span>
								<p className="text-lg font-medium mb-2">
									{t('viewer.transcript.waitingForWriter')}
								</p>
								<p className="opacity-80">{t('viewer.transcript.waitingForWriterDesc')}</p>
							</div>
						) : localError && !document && !polledRawHtml ? (
							<div className="flex flex-col items-center justify-center h-64 text-center">
								<WifiOff size={48} className="mb-4 opacity-30" />
								<p className="text-lg font-medium mb-2">{t('viewer.transcript.sourceError')}</p>
								<p className="opacity-80 mb-4">{localError}</p>
								<button
									className="px-4 py-2 rounded-lg border hover:bg-black/10 transition-colors"
									style={{ borderColor: `${settings.textColor}40` }}
									onClick={localRefresh}
								>
									{t('viewer.transcript.tryAgain')}
								</button>
							</div>
						) : polledRawHtml ? (
							/* Raw HTML from source — sanitised through DOMPurify before
							   it reaches the inner-HTML render so a compromised upstream
							   URL can't inject scripts. */
							<div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(polledRawHtml) }} />
						) : (
							<TranscriptParagraphs
								document={document}
								activeParagraphId={null}
								isWriterTyping={false}
								guardSegmentColors={guardSegmentColors}
								textColor={settings.textColor}
								typingLabel={t('viewer.transcript.writerTyping')}
							/>
						)
					) : /* Cloud mode: connecting / waiting / paragraphs with typing indicator */
					!document && !isConnecting && !isConnected ? (
						<div className="flex flex-col items-center justify-center h-64 text-center">
							<WifiOff size={48} className="mb-4 opacity-30" />
							<p className="text-lg font-medium mb-2">{t('viewer.transcript.notConnected')}</p>
							<p className="opacity-80 mb-4">{t('viewer.transcript.notConnectedDesc')}</p>
							<button
								className="px-4 py-2 rounded-lg border hover:bg-black/10 transition-colors"
								style={{ borderColor: `${settings.textColor}40` }}
								onClick={reconnect}
							>
								{t('viewer.transcript.tryAgain')}
							</button>
						</div>
					) : !document && (isConnecting || (isConnected && !hasWriter)) ? (
						<div className="flex flex-col items-center justify-center h-64 text-center">
							<span className="mb-4 opacity-30">
								<Spinner size="lg" />
							</span>
							<p className="text-lg font-medium mb-2">
								{isConnecting
									? t('viewer.transcript.connecting')
									: t('viewer.transcript.waitingForWriter')}
							</p>
							<p className="opacity-80">
								{isConnecting
									? t('viewer.transcript.connectingDesc')
									: t('viewer.transcript.waitingForWriterDesc')}
							</p>
						</div>
					) : (
						/* The same element as the polled branch above, so a transport
						   switch updates the paragraphs instead of remounting all of
						   them (MU N3c, 7. 10. 2026). */
						<TranscriptParagraphs
							document={document}
							activeParagraphId={activeParagraphId}
							isWriterTyping={isWriterTyping}
							guardSegmentColors={guardSegmentColors}
							textColor={settings.textColor}
							typingLabel={t('viewer.transcript.writerTyping')}
						/>
					)}
				</div>
			</main>

			{/*
				F-017: Off-screen polite live region. Receives only newly-appended
				transcript text (debounced) so screen readers announce the delta
				instead of re-reading the whole region on each keystroke. The visible
				<main> above is a passive role="region" — never live — so the SR only
				ever announces from here.
			*/}
			<div
				ref={announcerRef}
				className="sr-only"
				aria-live="polite"
				aria-atomic="false"
				aria-relevant="additions"
			/>
		</div>
	)
}

interface TranscriptParagraphsProps {
	document: DocumentState | null
	/** Paragraph the writer is typing in (WebSocket writer); null otherwise. */
	activeParagraphId: string | null
	isWriterTyping: boolean
	guardSegmentColors: ReturnType<typeof useSegmentColorGuard>
	textColor: string
	typingLabel: string
}

/**
 * The transcript's paragraphs, plus the "writer is typing" line. One component
 * for every transport (local, HTTP fallback, socket): the polled and the socket
 * branches used to render different element trees, so each switch between
 * them — every HTTP-fallback start and end — remounted every paragraph, losing
 * text selection and the screen reader's place (MU N3c, 7. 10. 2026).
 */
function TranscriptParagraphs({
	document,
	activeParagraphId,
	isWriterTyping,
	guardSegmentColors,
	textColor,
	typingLabel,
}: TranscriptParagraphsProps) {
	return (
		<>
			{document?.paragraphs.map((paragraph) => {
				const Tag = paragraph.headingLevel
					? (`h${paragraph.headingLevel}` as 'h1' | 'h2' | 'h3')
					: 'p'
				const isActive = activeParagraphId === paragraph.id && isWriterTyping
				return (
					<Tag
						key={paragraph.id}
						className={`mb-4 transition-all duration-200 ${
							isActive ? 'border-l-2 border-primary/40 pl-3' : ''
						}`}
					>
						{paragraph.segments.map((segment, i) => (
							<span
								key={i}
								style={{
									fontWeight: segment.bold ? 'bold' : undefined,
									fontStyle: segment.italic ? 'italic' : undefined,
									textDecoration: segment.underline ? 'underline' : undefined,
									verticalAlign: segment.subscript
										? 'sub'
										: segment.superscript
											? 'super'
											: undefined,
									fontSize: segment.subscript || segment.superscript ? '0.8em' : undefined,
									...guardSegmentColors(segment.color, segment.backgroundColor),
								}}
							>
								{segment.text}
							</span>
						))}
						{isActive && (
							<span
								className="inline-block w-0.5 h-[1em] bg-primary/60 animate-pulse ml-0.5 align-text-bottom"
								aria-hidden="true"
							/>
						)}
					</Tag>
				)
			})}
			{isWriterTyping && (
				// F-016: opacity-80 keeps the typing indicator visually
				// subordinate while passing WCAG 1.4.3 (~7:1 effective).
				// aria-hidden — the off-screen announcer already signals new
				// text; the visual dots are decorative.
				<p
					className="text-sm opacity-80 flex items-center gap-2"
					style={{ color: textColor }}
					aria-hidden="true"
				>
					<span className="flex gap-0.5" aria-hidden="true">
						<span
							className="w-1 h-1 rounded-full bg-current animate-bounce"
							style={{ animationDelay: '0ms' }}
						/>
						<span
							className="w-1 h-1 rounded-full bg-current animate-bounce"
							style={{ animationDelay: '150ms' }}
						/>
						<span
							className="w-1 h-1 rounded-full bg-current animate-bounce"
							style={{ animationDelay: '300ms' }}
						/>
					</span>
					{typingLabel}
				</p>
			)}
		</>
	)
}
